import { MAX_CHARS, MAX_SENTENCES, splitSentences } from './sentences';

/**
 * What a rubric is and what a score request may contain. Pure. The rubrics that ship live
 * in `library.ts`.
 */

export interface RubricLevel {
  name: string;
  /** What writing at this level looks like — a situation, not a degree. */
  text: string;
}

export interface RubricItem {
  id: string;
  name: string;
  description: string;
  /** Ordered low to high. Jev's Score primitive takes the same order. */
  levels: RubricLevel[];
}

export interface Rubric {
  version: 1;
  title: string;
  /** The task and source material, kept separate from the writing being graded. */
  assignment?: string;
  /** Index into `levels`. An item passes when enough probability sits at or above it. */
  passLevel: number;
  items: RubricItem[];
}

export const MIN_LEVELS = 2;
export const MAX_LEVELS = 6;
export const MAX_ITEMS = 8;
export const MAX_ITEM_TEXT = 2_000;
export const MAX_TITLE = 200;

export const DEFAULT_LEVEL_NAMES = ['Beginning', 'Developing', 'Proficient', 'Exemplary'] as const;

export type ValidationError =
  | 'text_empty'
  | 'text_too_long'
  | 'too_many_sentences'
  | 'too_many_items'
  | 'no_items'
  | 'item_name_empty'
  | 'item_text_too_long'
  | 'title_too_long'
  | 'bad_level_count'
  | 'bad_pass_level'
  | 'malformed';

export type Validation =
  | { ok: true }
  | { ok: false; error: ValidationError; limit?: number };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Structural check: is this a Rubric at all? Limits are checked separately. */
export function isRubric(value: unknown): value is Rubric {
  if (!isRecord(value)) return false;
  if (value.version !== 1) return false;
  if (typeof value.title !== 'string') return false;
  if (value.assignment !== undefined && typeof value.assignment !== 'string') return false;
  if (typeof value.passLevel !== 'number' || !Number.isInteger(value.passLevel)) return false;
  if (!Array.isArray(value.items)) return false;
  return value.items.every((item) =>
    isRecord(item) &&
    typeof item.id === 'string' &&
    typeof item.name === 'string' &&
    typeof item.description === 'string' &&
    Array.isArray(item.levels) &&
    item.levels.every((l) => isRecord(l) && typeof l.name === 'string' && typeof l.text === 'string'));
}

export function validateScoreRequest(body: unknown): Validation {
  if (!isRecord(body) || typeof body.text !== 'string' || !isRubric(body.rubric)) {
    return { ok: false, error: 'malformed' };
  }
  const { text, rubric } = body;

  if ((rubric.assignment?.length ?? 0) > MAX_CHARS) return { ok: false, error: 'item_text_too_long', limit: MAX_CHARS };
  if (text.trim().length === 0) return { ok: false, error: 'text_empty' };
  if (text.length > MAX_CHARS) return { ok: false, error: 'text_too_long', limit: MAX_CHARS };
  if (rubric.title.length > MAX_TITLE) return { ok: false, error: 'title_too_long', limit: MAX_TITLE };
  if (rubric.items.length === 0) return { ok: false, error: 'no_items' };
  if (rubric.items.length > MAX_ITEMS) return { ok: false, error: 'too_many_items', limit: MAX_ITEMS };

  for (const item of rubric.items) {
    if (item.name.trim().length === 0) return { ok: false, error: 'item_name_empty' };
    if (item.levels.length < MIN_LEVELS || item.levels.length > MAX_LEVELS) {
      return { ok: false, error: 'bad_level_count', limit: MAX_LEVELS };
    }
    const texts = [item.name, item.description, ...item.levels.flatMap((l) => [l.name, l.text])];
    if (texts.some((t) => t.length > MAX_ITEM_TEXT)) {
      return { ok: false, error: 'item_text_too_long', limit: MAX_ITEM_TEXT };
    }
    if (rubric.passLevel < 0 || rubric.passLevel >= item.levels.length) {
      return { ok: false, error: 'bad_pass_level' };
    }
  }

  if (splitSentences(text).length > MAX_SENTENCES) {
    return { ok: false, error: 'too_many_sentences', limit: MAX_SENTENCES };
  }
  return { ok: true };
}
